import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { BRAND_THEME } from "@/lib/constants";
import { Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const OutletDialog = ({ open, onClose, brand, outlet, onSaved }) => {
  const [name, setName] = useState(outlet?.name || "");
  const [aliases, setAliases] = useState((outlet?.aliases || []).join(", "));
  const [active, setActive] = useState(outlet?.active !== false);
  const save = async () => {
    const body = { brand, name, aliases: aliases.split(","), active };
    try {
      outlet ? await api.put(`/outlets/${outlet.id}`, body) : await api.post("/outlets", body);
      toast.success("Outlet saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="outlet-dialog">
        <DialogHeader><DialogTitle>{outlet ? "Edit outlet" : `Add outlet to ${brand}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Outlet name</Label><Input data-testid="outlet-name-input" className="h-11 rounded-xl" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="space-y-2">
            <Label>Other spellings (aliases)</Label>
            <Input data-testid="outlet-aliases-input" className="h-11 rounded-xl" value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="e.g. Ninis Kitchen - Indiranagar, NK IND" />
            <p className="text-xs text-slate-500">Separate with commas. Use the names Reelo, Zomato, Swiggy or Google show for this outlet.</p>
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={active} onCheckedChange={setActive} data-testid="outlet-active-switch" />Active</label>
          <Button className="h-11 w-full rounded-xl" onClick={save} data-testid="outlet-save-button">Save outlet</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function OutletsTab() {
  const { meta, refreshMeta } = useApp();
  const [dlg, setDlg] = useState(null);
  if (!meta) return null;
  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="outlets-tab">
      {meta.brands.map((b) => {
        const t = BRAND_THEME[b];
        const list = meta.outlets.filter((o) => o.brand === b);
        return (
          <Card key={b} testid={`brand-card-${b.replace(/[^a-z]/gi, "").toLowerCase()}`}
            title={<span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: t.primary }} />{b}</span>}
            subtitle={`${list.length} outlets`}
            right={<Button size="sm" variant="outline" className="rounded-lg" onClick={() => setDlg({ brand: b })} data-testid={`add-outlet-${b.replace(/[^a-z]/gi, "").toLowerCase()}`}><Plus className="mr-1 h-4 w-4" />Add</Button>}>
            <ul className="space-y-2">
              {list.map((o) => (
                <li key={o.id} className="flex items-start justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800">{o.name}{o.active === false && <span className="ml-2 text-xs text-slate-400">(inactive)</span>}</div>
                    <div className="truncate text-xs text-slate-500">{o.aliases?.length ? `Also: ${o.aliases.join(", ")}` : "No other spellings"}</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => setDlg({ brand: b, outlet: o })} data-testid={`edit-outlet-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                </li>
              ))}
            </ul>
          </Card>
        );
      })}
      {dlg && <OutletDialog open onClose={() => setDlg(null)} brand={dlg.brand} outlet={dlg.outlet} onSaved={refreshMeta} />}
    </div>
  );
}
